import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Skeleton,
  Alert,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  Autocomplete,
} from '@mui/material';
import {
  Search as SearchIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Launch as LaunchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useGetEnstruLookupQuery, useGetStaticSpecsQuery, useGetStaticCategoriesQuery } from '@/services/api';
import type { SpecInfo, CategoryInfo } from '@/types/api';
import { useSelector } from 'react-redux';
import type { RootState } from '@/store';

type SortField = 'lot_id' | 'category' | 'validation_status' | 'size' | 'last_modified';
type SortDirection = 'asc' | 'desc';

const SpecsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<CategoryInfo | null>(null);
  const [validationStatus, setValidationStatus] = useState('');
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortField>('last_modified');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const { language } = useSelector((state: RootState) => state.ui);
  const perPage = 50;

  // Load all specs from static CDN file (fast!)
  const {
    data: allSpecs = [],
    error,
    isLoading,
  } = useGetStaticSpecsQuery();

  // Load categories from static CDN file (fast!)
  const {
    data: categories = [],
  } = useGetStaticCategoriesQuery();
  const { data: enstruLookup } = useGetEnstruLookupQuery();

  const specs = allSpecs;
  const normalizeEnstruCode = (code: string | undefined) => (code ? code.replace(/^ENSTRU:/, '') : '');

  // Function to get category name by matching the category code
  const getCategoryName = (categoryCode: string | undefined): string => {
    if (!categoryCode) return '-';
    
    // Remove ENSTRU: prefix if present
    const cleanCode = normalizeEnstruCode(categoryCode);
    
    // Find matching category by id
    const matchingCategory = categories.find(cat => cat.id === cleanCode);
    if (matchingCategory) {
      return language === 'en' ? matchingCategory.name_en : matchingCategory.name_ru;
    }
    
    return '-';
  };

  // Filter and sort specs client-side for better filtering support
  const filteredSpecs = specs
    .filter((spec) => {
      // Search filter - check lot_id, filename, and category names
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesLotId = spec.lot_id.toLowerCase().includes(searchLower);
        const matchesFilename = spec.filename.toLowerCase().includes(searchLower);
        
        // Check category names (both EN and RU)
        let matchesCategoryName = false;
        if (spec.category) {
          const cleanCategoryId = normalizeEnstruCode(spec.category);
          const categoryName = getCategoryName(spec.category);
          matchesCategoryName = categoryName.toLowerCase().includes(searchLower);
          
          // Also try to find category in the categories list to check both names
          const categoryInfo = categories.find(cat => cat.id === cleanCategoryId);
          if (categoryInfo) {
            matchesCategoryName = matchesCategoryName || 
              categoryInfo.name_en.toLowerCase().includes(searchLower) ||
              categoryInfo.name_ru.toLowerCase().includes(searchLower);
          }
        }
        
        if (!matchesLotId && !matchesFilename && !matchesCategoryName) {
          return false;
        }
      }
      
      // Category filter
      if (selectedCategory && spec.category) {
        const cleanCategory = normalizeEnstruCode(spec.category);
        if (cleanCategory !== selectedCategory.id) {
          return false;
        }
      }

      // Validation status filter
      if (validationStatus && spec.validation_status !== validationStatus) {
        return false;
      }

      return true;
    })
    .sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      // Handle different data types
      if (sortField === 'last_modified') {
        aValue = new Date(aValue || 0).getTime();
        bValue = new Date(bValue || 0).getTime();
      } else if (sortField === 'size') {
        aValue = aValue || 0;
        bValue = bValue || 0;
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = (bValue || '').toLowerCase();
      }

      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

  // Client-side pagination
  const totalFilteredSpecs = filteredSpecs.length;
  const totalPages = Math.ceil(totalFilteredSpecs / perPage);
  const startIndex = (page - 1) * perPage;
  const endIndex = startIndex + perPage;
  const filteredAndSortedSpecs = filteredSpecs.slice(startIndex, endIndex);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSpecClick = (lotId: string, openInNewTab: boolean = false) => {
    if (openInNewTab) {
      // Open in new tab
      window.open(`/spec/${lotId}`, '_blank');
    } else {
      // Navigate in same tab
      navigate(`/spec/${lotId}`);
    }
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const getValidationIcon = (status?: string) => {
    switch (status) {
      case 'ok':
        return <CheckIcon color="success" sx={{ fontSize: 18 }} />;
      case 'fail':
        return <ErrorIcon color="error" sx={{ fontSize: 18 }} />;
      case 'partial':
        return <DescriptionIcon color="warning" sx={{ fontSize: 18 }} />;
      default:
        return <DescriptionIcon color="action" sx={{ fontSize: 18 }} />;
    }
  };

  const getValidationColor = (status?: string) => {
    switch (status) {
      case 'ok':
        return 'success';
      case 'fail':
        return 'error';
      case 'partial':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (error) {
    const errorMessage = 'data' in error && error.data 
      ? (error.data as any).message || (language === 'en' ? 'Failed to load specifications' : 'Не удалось загрузить спецификации')
      : (language === 'en' ? 'Failed to load specifications' : 'Не удалось загрузить спецификации');
    
    return (
      <Box sx={{ p: 3, width: '100%', maxWidth: 'none', minWidth: '100%' }}>
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            {language === 'en' ? 'Error Loading Specifications' : 'Ошибка загрузки спецификаций'}
          </Typography>
          <Typography variant="body2">
            {errorMessage}
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, width: '100%', maxWidth: 'none', minWidth: '100%' }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {language === 'en' ? 'Parsed Specifications' : 'Разобранные спецификации'}
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          {language === 'en' 
            ? 'Browse and explore parsed technical specifications with their extracted properties and validation status.'
            : 'Просматривайте и изучайте разобранные технические спецификации с их извлеченными свойствами и статусом валидации.'
          }
        </Typography>

        {/* Search and Filters */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <TextField
            variant="outlined"
            placeholder={language === 'en' ? 'Search by lot ID...' : 'Поиск по ID лота...'}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1); // Reset to first page when searching
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1, maxWidth: 400 }}
          />
          
          <Autocomplete
            sx={{ minWidth: 300 }}
            options={categories}
            value={selectedCategory}
            onChange={(event, newValue) => {
              setSelectedCategory(newValue);
              setPage(1); // Reset to first page when filtering
            }}
            getOptionLabel={(option) => 
              `${language === 'en' ? option.name_en : option.name_ru} (${option.id})`
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={language === 'en' ? 'Category' : 'Категория'}
                placeholder={language === 'en' ? 'Search categories...' : 'Поиск категорий...'}
                variant="outlined"
              />
            )}
            filterOptions={(options, { inputValue }) => {
              const filtered = options.filter((option) => {
                const nameEn = (option.name_en || '').toLowerCase();
                const nameRu = (option.name_ru || '').toLowerCase();
                const id = (option.id || '').toLowerCase();
                const input = inputValue.toLowerCase();

                return nameEn.includes(input) || nameRu.includes(input) || id.includes(input);
              });
              return filtered.slice(0, 50); // Limit to 50 results for performance
            }}
            clearOnBlur={false}
            selectOnFocus
            handleHomeEndKeys
          />

          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>{language === 'en' ? 'Validation Status' : 'Статус валидации'}</InputLabel>
            <Select
              value={validationStatus}
              label={language === 'en' ? 'Validation Status' : 'Статус валидации'}
              onChange={(e) => {
                setValidationStatus(e.target.value);
                setPage(1); // Reset to first page when filtering
              }}
            >
              <MenuItem value="">
                {language === 'en' ? 'All Statuses' : 'Все статусы'}
              </MenuItem>
              <MenuItem value="ok">
                {language === 'en' ? 'OK' : 'OK'}
              </MenuItem>
              <MenuItem value="partial">
                {language === 'en' ? 'Partial' : 'Частично'}
              </MenuItem>
              <MenuItem value="fail">
                {language === 'en' ? 'Failed' : 'Неудачно'}
              </MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Box>

      {/* Specifications Table */}
      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>{language === 'en' ? 'Filename' : 'Имя файла'}</strong>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'category'}
                  direction={sortField === 'category' ? sortDirection : 'asc'}
                  onClick={() => handleSort('category')}
                >
                  <strong>{language === 'en' ? 'Category' : 'Категория'}</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <strong>{language === 'en' ? 'Properties' : 'Свойства'}</strong>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'size'}
                  direction={sortField === 'size' ? sortDirection : 'asc'}
                  onClick={() => handleSort('size')}
                >
                  <strong>{language === 'en' ? 'File Size' : 'Размер файла'}</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'last_modified'}
                  direction={sortField === 'last_modified' ? sortDirection : 'asc'}
                  onClick={() => handleSort('last_modified')}
                >
                  <strong>{language === 'en' ? 'Last Modified' : 'Последнее изменение'}</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <strong>{language === 'en' ? 'Actions' : 'Действия'}</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              // Loading skeletons
              Array.from({ length: 10 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell><Skeleton variant="text" width="80%" /></TableCell>
                  <TableCell><Skeleton variant="text" width="70%" /></TableCell>
                  <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                  <TableCell><Skeleton variant="text" width="40%" /></TableCell>
                  <TableCell><Skeleton variant="text" width="70%" /></TableCell>
                  <TableCell><Skeleton variant="circular" width={24} height={24} /></TableCell>
                </TableRow>
              ))
            ) : filteredAndSortedSpecs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Alert severity="info">
                    {searchTerm || selectedCategory || validationStatus
                      ? (language === 'en' ? 'No specifications match your criteria.' : 'Спецификации не найдены по вашим критериям.')
                      : (language === 'en' ? 'No specifications found.' : 'Спецификации не найдены.')
                    }
                  </Alert>
                </TableCell>
              </TableRow>
            ) : (
              filteredAndSortedSpecs.map((spec: SpecInfo) => (
                <TableRow
                  key={spec.lot_id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    // Check if Ctrl/Cmd or middle mouse button was pressed
                    const openInNewTab = e.ctrlKey || e.metaKey || e.button === 1;
                    handleSpecClick(spec.lot_id, openInNewTab);
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{
                      fontSize: '0.875rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '250px'
                    }}>
                      {spec.filename}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {spec.category ? (
                      <Box>
                        {(() => {
                          const cleanCode = normalizeEnstruCode(spec.category);
                          const details = enstruLookup?.[cleanCode];
                          return (
                            <>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {getCategoryName(spec.category) !== '-' ? getCategoryName(spec.category) : details?.name || '-'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', display: 'block' }}>
                                {cleanCode}
                              </Typography>
                              {details?.characteristic && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    display: 'block',
                                    maxWidth: '260px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {details.characteristic}
                                </Typography>
                              )}
                            </>
                          );
                        })()}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {(spec.playbook_properties || spec.additional_properties) ? (
                      <Typography variant="body2">
                        {spec.playbook_properties || 0}
                        {spec.additional_properties > 0 && (
                          <Typography component="span" color="success.main" sx={{ ml: 0.5, fontWeight: 500 }}>
                            +{spec.additional_properties} {language === 'en' ? 'new' : 'новых'}
                          </Typography>
                        )}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {(spec.size / 1024).toFixed(1)} KB
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {(() => {
                        // Parse the date string properly - if no timezone, assume UTC
                        const dateStr = spec.last_modified;
                        // Remove milliseconds if present
                        const cleanDateStr = dateStr.replace(/\.\d{3}/, '');
                        const date = cleanDateStr.includes('T') && !cleanDateStr.includes('Z') && !cleanDateStr.includes('+') 
                          ? new Date(cleanDateStr + 'Z') // Add Z to treat as UTC
                          : new Date(cleanDateStr);
                        
                        if (isNaN(date.getTime())) return '-';
                        
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const day = String(date.getDate()).padStart(2, '0');
                        const hours = String(date.getHours()).padStart(2, '0');
                        const minutes = String(date.getMinutes()).padStart(2, '0');
                        const seconds = String(date.getSeconds()).padStart(2, '0');
                        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
                      })()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <LaunchIcon
                      color="primary"
                      sx={{ fontSize: 20, cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSpecClick(spec.lot_id, true); // Always open in new tab
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination and Summary */}
      {specs.length > 0 && (
        <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
            />
          )}
          
          <Typography variant="body2" color="text.secondary" align="center">
            {language === 'en' 
              ? `Showing ${filteredAndSortedSpecs.length} of ${totalFilteredSpecs} specifications`
              : `Показано ${filteredAndSortedSpecs.length} из ${totalFilteredSpecs} спецификаций`
            }
            {(searchTerm || selectedCategory || validationStatus) && (language === 'en' ? ' matching your criteria' : ' соответствующих вашим критериям')}
            {specs.length > totalFilteredSpecs && (
              <span>
                {language === 'en' 
                  ? ` (filtered from ${specs.length} total)`
                  : ` (отфильтровано из ${specs.length} всего)`
                }
              </span>
            )}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SpecsPage;
